import React, { useEffect, useRef, useState } from "react";
import { Check, Loader2, Square } from "lucide-react";
import { Button } from "../../ui/button";

// Seconds under a minute, then m:ss, so a long wait still reads at a glance.
export const formatElapsed = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = String(seconds % 60).padStart(2, "0");
  return `${m}:${s}`;
};

/** What the dialog shows while a session is being generated. */
const GenerationProgress = ({
  isLoading,
  generationStatus,
  thinkingText,
  takeFirstPlan,
  cancelGeneration,
}) => {
  const [elapsed, setElapsed] = useState(0);
  const elapsedRef = useRef(null);

  useEffect(() => {
    if (isLoading) {
      setElapsed(0);
      elapsedRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } else {
      clearInterval(elapsedRef.current);
    }
    return () => clearInterval(elapsedRef.current);
  }, [isLoading]);

  const thinkingEndRef = useRef(null);
  useEffect(() => {
    if (thinkingText && thinkingEndRef.current) {
      thinkingEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [thinkingText]);

  const waitingHint = (() => {
    if (!isLoading) return null;
    // The first plan is ready but missed a check, and a second call is
    // fixing it (#166).
    if (takeFirstPlan)
      return "The first plan missed a check, so the AI is fixing it. You can use the first plan as it is.";
    if (thinkingText != null) return null;
    const isSending = generationStatus?.startsWith("Sending");
    if (!isSending) return null;
    // The elapsed counter keeps running across tries, so the hints below
    // (which assume a first try) would say "almost there" on a retry.
    if (generationStatus.includes("(retry"))
      return "The last try didn't work out, so this is a fresh one.";
    if (elapsed < 5) return "Connecting to AI provider...";
    if (elapsed < 12) return "AI is analyzing your workout requirements...";
    if (elapsed < 20)
      return "Thinking through exercise selection and timing...";
    if (elapsed < 35) return "Building your personalized workout structure...";
    if (elapsed < 50) return "Generating detailed exercise plan...";
    if (elapsed < 70) return "Almost there, finalizing workout...";
    return "This is taking longer than usual. If the AI goes quiet, Khronos tries again by itself.";
  })();

  return (
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden space-y-6 py-4 w-full min-w-0">
            <div className="flex items-center gap-4 px-2">
              <Loader2 className="h-6 w-6 animate-spin text-primary shrink-0 drop-shadow-[0_0_8px_rgba(var(--primary),0.5)]" />
              <div className="flex-1 min-w-0">
                <span className="text-base font-semibold tracking-wide block text-foreground truncate">
                  {generationStatus || "Generating..."}
                </span>
                {waitingHint && (
                  <span className="text-sm text-muted-foreground block mt-1 transition-opacity duration-500">
                    {waitingHint}
                  </span>
                )}
              </div>
              <span className="text-sm font-mono text-primary/80 shrink-0 tabular-nums">
                {formatElapsed(elapsed)}
              </span>
            </div>

            {thinkingText != null && (
              <div className="thinking-terminal rounded-md p-4 max-h-56 overflow-y-auto text-xs leading-relaxed whitespace-pre-wrap bg-black/40 border border-primary/30 shadow-[inset_0_0_20px_rgba(0,0,0,0.5)] font-mono text-primary/70 min-w-0">
                <span className="relative z-10">
                  {thinkingText || (
                    <span className="opacity-50 animate-pulse">
                      Initializing neural link...
                    </span>
                  )}
                  <span
                    className="thinking-cursor bg-primary w-2 h-4 inline-block ml-1 align-middle animate-pulse"
                    ref={thinkingEndRef}
                  />
                </span>
              </div>
            )}

            <div className="w-full bg-muted/50 rounded-full h-2 overflow-hidden border border-border/50">
              <div
                className="bg-primary h-full rounded-full animate-pulse shadow-[0_0_10px_rgba(var(--primary),0.6)]"
                style={{ width: "60%" }}
              />
            </div>
            <div className="flex flex-wrap justify-center gap-2 pt-2">
              {takeFirstPlan && (
                <Button type="button" size="sm" onClick={takeFirstPlan}>
                  <Check className="mr-2 h-3 w-3" />
                  Use the first plan
                </Button>
              )}
              <Button
                type="button"
                // Quieter beside "Use the first plan", so the two don't compete.
                variant={takeFirstPlan ? "outline" : "destructive"}
                size="sm"
                className={
                  takeFirstPlan
                    ? undefined
                    : "shadow-[0_0_10px_rgba(var(--destructive),0.2)]"
                }
                onClick={cancelGeneration}
              >
                <Square className="mr-2 h-3 w-3 fill-current" />
                Stop generating
              </Button>
            </div>
          </div>
  );
};

export default GenerationProgress;
